import React from 'react';
import PropTypes from 'prop-types';
import { DataGrid, PageTitleWithToolTip } from '../../Common';
import {
    StyledCellLink,
    Percentage,
    FractionPercentage,
    Weight
} from '../../Common/DataGrid/CellComponents';
import {
    getDateRangeFilterOperator,
    getRangeFilterOperator
} from '../../Common/DataGrid/FilterComponents';
import { text_commonCellProps } from '../../Common/DataGrid/utils';
import { TranslateW } from '../../../Utils/Languages/Translate';
import Routes from '../../../routes';
import { CardHeader } from '@mui/material';
import { isDesktop } from '../../../utils';

const dateRangeFilterOperator = getDateRangeFilterOperator({});
const rangeFilterOperator = getRangeFilterOperator({});

const columns = [
    {
        field: 'workOrderNumberAndWorkItemId',
        headerName: 'Service Event ID',
        ...text_commonCellProps,
        renderCell: ({ row: { id, workOrderNumberAndWorkItemId } }) => (
            <StyledCellLink
                path={`/ServiceEvent/${id}`}
                text={workOrderNumberAndWorkItemId}
                from={window.location.pathname}
            />
        )
    },
    {
        field: 'serviceDate',
        headerName: 'Service Date',
        filterOperators: dateRangeFilterOperator,
    },
    {
        field: 'workItemStatusName',
        headerName: 'Status',
        ...text_commonCellProps
    },
    {
        field: 'workItemTypeName',
        headerName: 'Type',
        ...text_commonCellProps
    },
    {
        field: 'primaryTechName',
        headerName: 'Primary Technician',
        ...text_commonCellProps
    },
    {
        field: 'assetName',
        headerName: 'Asset Name',
        ...text_commonCellProps
    },
]

const netGasLbsAddedCylinder = {
    field: 'netGasAdded',
    headerName: 'Gas Transfer',
    filterOperators: rangeFilterOperator,
    renderCell: ({ row: { netGasAdded } }) => <Weight value={netGasAdded} />
}

const netGasLbsAddedRefrigerant = {
    field: 'netGasAdded',
    headerName: 'Net Gas Added',
    filterOperators: rangeFilterOperator,
    renderCell: ({ row: { netGasAdded } }) => <Weight value={netGasAdded} />
}

const columnFooter = [
    {
        field: 'leakRatio',
        headerName: 'Leak Rate %',
        ...text_commonCellProps,
        renderCell: ({ row: { leakRatio } }) => <Percentage value={leakRatio} />
    },
    {
        // AB#14852: EPA Rolling Average leak rate as of the SE date, per-page from the DWH.
        // Not sortable/filterable: the value is enriched onto the returned page only.
        field: 'epaRollingLeakRate',
        headerName: 'Leak Rate - EPA Rolling Avg (%)',
        sortable: false,
        filterable: false,
        renderCell: ({ row: { epaRollingLeakRate } }) => <FractionPercentage value={epaRollingLeakRate} />
    },
    {
        // AB#16788: true trailing-365-day rolling average as of the SE date, per-page from the DWH.
        // Not sortable/filterable: the value is enriched onto the returned page only.
        field: 'twelveMonthRollingLeakRate',
        headerName: '12-Month Average Leak Rate (%)',
        sortable: false,
        filterable: false,
        renderCell: ({ row: { twelveMonthRollingLeakRate } }) => <FractionPercentage value={twelveMonthRollingLeakRate} />
    },
    {
        // AB#16849: highest (peak) EPA leak rate over the trailing 12 months, from the
        // non-resetting DWH fact table (AB#15322 semantics) — same value on every row of the asset.
        field: 'highestEpaLeakRateLast12Months',
        headerName: 'Highest EPA Leak Rate - Last 12 Months',
        initiallyVisible: false,
        sortable: false,
        filterable: false,
        renderCell: ({ row: { highestEpaLeakRateLast12Months } }) => <FractionPercentage value={highestEpaLeakRateLast12Months} />
    },
    {
        // AB#16849: highest (peak) EPA leak rate since Jan 1 of the current calendar year.
        field: 'highestEpaLeakRateCalendarYear',
        headerName: 'Highest EPA Leak Rate - Calendar Year',
        initiallyVisible: false,
        sortable: false,
        filterable: false,
        renderCell: ({ row: { highestEpaLeakRateCalendarYear } }) => <FractionPercentage value={highestEpaLeakRateCalendarYear} />
    },
    {
        field: 'leakRepaired',
        headerName: 'Leak Repaired',
        ...text_commonCellProps,
    },
    {
        field: 'underRepairEventId',
        headerName: 'Under Repair Service Event',
        ...text_commonCellProps,
        renderCell: ({ row: { underRepairEventId } }) =>
                 <StyledCellLink
                    path={`/ServiceEvent/${underRepairEventId}`}
                    text={underRepairEventId}
                    from={window.location.pathname}
                />
    }
]

const ServiceEventTable = ({
    assetID = '',
    isCylinder = false
}) => {
    const netGasAddedColumn = isCylinder ? netGasLbsAddedCylinder : netGasLbsAddedRefrigerant;
    return (
        <>
            <DataGrid
                url={Routes.Asset.ServiceEventHistory(assetID)}
                exportButtonURL={Routes.Asset.ServiceEventHistoryExport(assetID)}
                exportButtonAlt={"Export service events (.csv)"}
                headerContent={
                    isDesktop && (
                        <CardHeader
                            title={
                                <PageTitleWithToolTip
                                    pageTitle={TranslateW("serviceActivityView.service-events")}
                                    toolTipText={TranslateW("ServiceEventsDescription")}
                                />
                            }
                            sx={{
                                "& .MuiCardHeader-action": { display: "flex" },
                                "& .MuiCardHeader-content": { maxWidth: "100%", textWrap: "nowrap" }
                            }}
                        />
                    )
                }
                noResultText="Oops...It is empty here..."
                noResultText2="Create a new Service Event or change your search criteria."
                columns={[...columns, netGasAddedColumn, ...columnFooter]}
            />
        </>
    )
}

ServiceEventTable.propTypes = {
    assetID: PropTypes.string.isRequired,
};

export default ServiceEventTable;
